import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  WaitlistConfig,
  WaitlistMachineState,
  WaitlistEntry,
  PendingBackfill,
  WaitlistSummary,
  BackfillPendingState,
  BackfillOutcomeEntry,
  BackfillNotificationState,
  SlotOpenedPayload,
  WaitlistStatePayload,
  BackfillMatchPayload,
  BackfillNoMatchPayload,
  BackfillOutcomePayload,
  WaitlistStateRequestPayload,
  WaitlistHandle,
} from './waitlistTypes';
import type { EmbeddedToHostMessage, DateRange } from '../types';

const AWAITING_STATE_TIMEOUT_MS = 5_000;
const BACKFILL_PENDING_TIMEOUT_MS = 10_000;

export function useWaitlist(
  waitlistConfig: WaitlistConfig | undefined,
  emit: (msg: EmbeddedToHostMessage) => void,
  viewRange: DateRange,
  visibleResourceIds: string[] | undefined,
): WaitlistHandle {
  const enabled = waitlistConfig?.enabled ?? false;

  const [machineState, setMachineState] = useState<WaitlistMachineState>('IDLE');
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [pendingBackfills, setPendingBackfills] = useState<PendingBackfill[]>([]);
  const [summary, setSummary] = useState<WaitlistSummary | null>(null);
  const [backfillPendingMap, setBackfillPendingMap] = useState<Map<string, BackfillPendingState>>(new Map());
  const [backfillOutcomeMap, setBackfillOutcomeMap] = useState<Map<string, BackfillOutcomeEntry>>(new Map());
  const [notification, setNotification] = useState<BackfillNotificationState | null>(null);
  const [highlightedEntryId, setHighlightedEntryId] = useState<string | null>(null);

  // Stable refs so callbacks always see the latest values without needing them in dep arrays.
  const waitlistConfigRef = useRef(waitlistConfig);
  useEffect(() => { waitlistConfigRef.current = waitlistConfig; }, [waitlistConfig]);

  const viewRangeRef = useRef(viewRange);
  useEffect(() => { viewRangeRef.current = viewRange; }, [viewRange]);

  const visibleResourceIdsRef = useRef(visibleResourceIds);
  useEffect(() => { visibleResourceIdsRef.current = visibleResourceIds; }, [visibleResourceIds]);

  // Ref tracks current state so timeout callbacks can check it without stale closure.
  const machineStateRef = useRef<WaitlistMachineState>('IDLE');

  // Per-slot backfill timeout handles.
  const backfillTimeouts = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  // ── Apply incoming waitlist state ─────────────────────────────────────────

  const applyWaitlistState = useCallback((state: WaitlistStatePayload) => {
    setEntries(state.entries);
    setPendingBackfills(state.pendingBackfills);
    setSummary(state.summary);
    machineStateRef.current = 'WAITLIST_ACTIVE';
    setMachineState('WAITLIST_ACTIVE');
  }, []);

  // ── Request state from host ───────────────────────────────────────────────

  const requestWaitlistState = useCallback(() => {
    const cfg = waitlistConfigRef.current;
    if (!cfg?.enabled) return;

    const vr = viewRangeRef.current;
    const resourceIds = visibleResourceIdsRef.current ?? [];

    const payload: WaitlistStateRequestPayload = {
      visibleRangeStart: vr.startUtc,
      visibleRangeEnd: vr.endUtc,
      resourceIds,
    };

    machineStateRef.current = 'AWAITING_STATE';
    setMachineState('AWAITING_STATE');
    emit({ type: 'WAITLIST_STATE_REQUEST', payload });

    const result = cfg.onRequestWaitlistState(payload);

    if (result && typeof (result as Promise<WaitlistStatePayload>).then === 'function') {
      (result as Promise<WaitlistStatePayload>)
        .then(applyWaitlistState)
        .catch(() => {
          if (machineStateRef.current === 'AWAITING_STATE') {
            machineStateRef.current = 'IDLE';
            setMachineState('IDLE');
          }
        });
      return;
    }

    // Postmessage mode: timeout if no WAITLIST_STATE arrives.
    const timerId = setTimeout(() => {
      if (machineStateRef.current === 'AWAITING_STATE') {
        machineStateRef.current = 'IDLE';
        setMachineState('IDLE');
      }
    }, AWAITING_STATE_TIMEOUT_MS);

    // Store so we can cancel on unmount if needed (handled in cleanup effect below).
    return () => clearTimeout(timerId);
  // applyWaitlistState is stable; emit is stable.
  }, [emit, applyWaitlistState]);

  // Request on mount and whenever the visible range or resource page changes.
  const prevRangeKeyRef = useRef('');
  useEffect(() => {
    if (!enabled) return;

    const resourceIds = visibleResourceIds ?? [];
    const key = `${viewRange.startUtc}|${viewRange.endUtc}|${resourceIds.join(',')}`;
    if (prevRangeKeyRef.current === key) return;
    prevRangeKeyRef.current = key;

    requestWaitlistState();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, viewRange.startUtc, viewRange.endUtc, visibleResourceIds?.join(',')]);

  // ── Offer expiry countdown — single 60s interval ──────────────────────────

  useEffect(() => {
    if (machineState !== 'WAITLIST_ACTIVE') return;

    const tick = () => {
      const now = new Date();

      setPendingBackfills((prev) => {
        const expired: PendingBackfill[] = [];
        const next = prev.filter((pb) => {
          if (pb.status === 'offered' && new Date(pb.expiresAt) <= now) {
            expired.push(pb);
            return false;
          }
          return true;
        });

        // Re-emit SLOT_OPENED for each expired offer so host can offer next candidate.
        for (const pb of expired) {
          const cfg = waitlistConfigRef.current;
          const vr = viewRangeRef.current;
          if (!cfg?.enabled) continue;

          const reopenPayload: SlotOpenedPayload = {
            slotId: crypto.randomUUID(),
            resourceId: pb.resourceId,
            start: pb.start,
            end: pb.end,
            duration: Math.round((new Date(pb.end).getTime() - new Date(pb.start).getTime()) / 60000),
            openedBy: 'external',
            visibleRangeStart: vr.startUtc,
            visibleRangeEnd: vr.endUtc,
          };
          emit({ type: 'SLOT_OPENED', payload: reopenPayload });
          cfg.onSlotOpened(reopenPayload);
        }

        return next;
      });
    };

    const id = setInterval(tick, 60_000);
    return () => clearInterval(id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineState, emit]);

  // ── Per-slot pending state management ─────────────────────────────────────

  const clearBackfillPending = useCallback((slotId: string) => {
    const t = backfillTimeouts.current.get(slotId);
    if (t !== undefined) {
      clearTimeout(t);
      backfillTimeouts.current.delete(slotId);
    }
    setBackfillPendingMap((prev) => {
      if (!prev.has(slotId)) return prev;
      const next = new Map(prev);
      next.delete(slotId);
      return next;
    });
  }, []);

  // ── Message handlers ───────────────────────────────────────────────────────

  const onWaitlistStateReceived = useCallback((payload: WaitlistStatePayload) => {
    applyWaitlistState(payload);
  }, [applyWaitlistState]);

  const onBackfillMatchReceived = useCallback((payload: BackfillMatchPayload) => {
    clearBackfillPending(payload.slotId);
    setNotification({ slotId: payload.slotId, match: payload });

    if (payload.matches.length > 0) {
      const topMatch = payload.matches[0];
      const pb: PendingBackfill = {
        id: crypto.randomUUID(),
        slotId: payload.slotId,
        resourceId: payload.resourceId,
        start: payload.start,
        end: payload.end,
        patientId: topMatch.patientId,
        offeredAt: new Date().toISOString(),
        expiresAt: topMatch.offerExpiresAt,
        status: 'offered',
      };
      setPendingBackfills((prev) => [...prev, pb]);
    }
  }, [clearBackfillPending]);

  const onBackfillNoMatchReceived = useCallback((payload: BackfillNoMatchPayload) => {
    clearBackfillPending(payload.slotId);
  }, [clearBackfillPending]);

  const handleSlotOpened = useCallback((payload: SlotOpenedPayload) => {
    const cfg = waitlistConfigRef.current;
    if (!cfg?.enabled || machineStateRef.current !== 'WAITLIST_ACTIVE') return;

    const { slotId } = payload;

    setBackfillPendingMap((prev) => {
      const next = new Map(prev);
      next.set(slotId, {
        slotId,
        resourceId: payload.resourceId,
        start: payload.start,
        end: payload.end,
        openedAt: Date.now(),
      });
      return next;
    });

    emit({ type: 'SLOT_OPENED', payload });

    const result = cfg.onSlotOpened(payload);
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      (result as Promise<BackfillMatchPayload | BackfillNoMatchPayload>)
        .then((response) => {
          if ('matches' in response) {
            onBackfillMatchReceived(response as BackfillMatchPayload);
          } else {
            onBackfillNoMatchReceived(response as BackfillNoMatchPayload);
          }
        })
        .catch(() => clearBackfillPending(slotId));
    }

    // 10s timeout: clear pending state if no host response.
    const existing = backfillTimeouts.current.get(slotId);
    if (existing !== undefined) clearTimeout(existing);

    const timeoutId = setTimeout(() => {
      backfillTimeouts.current.delete(slotId);
      setBackfillPendingMap((prev) => {
        if (!prev.has(slotId)) return prev;
        const next = new Map(prev);
        next.delete(slotId);
        return next;
      });
    }, BACKFILL_PENDING_TIMEOUT_MS);

    backfillTimeouts.current.set(slotId, timeoutId);
  }, [emit, onBackfillMatchReceived, onBackfillNoMatchReceived, clearBackfillPending]);

  const onBackfillOutcomeReceived = useCallback((payload: BackfillOutcomePayload) => {
    waitlistConfigRef.current?.onBackfillOutcome?.(payload);

    // Remove the pending backfill offer from the list.
    setPendingBackfills((prev) => prev.filter((pb) => pb.slotId !== payload.slotId));
    clearBackfillPending(payload.slotId);

    // Track rendered outcome on the slot for 30s.
    if (payload.outcome === 'accepted' || payload.outcome === 'no_response') {
      setBackfillOutcomeMap((prev) => {
        const next = new Map(prev);
        next.set(payload.slotId, {
          slotId: payload.slotId,
          resourceId: payload.resourceId,
          outcome: payload.outcome as 'accepted' | 'no_response',
        });
        return next;
      });
      setTimeout(() => {
        setBackfillOutcomeMap((prev) => {
          if (!prev.has(payload.slotId)) return prev;
          const next = new Map(prev);
          next.delete(payload.slotId);
          return next;
        });
      }, 30_000);
    }

    // When accepted, remove the filled entry from the visible list.
    if (payload.outcome === 'accepted') {
      setEntries((prev) => prev.filter((e) => e.id !== payload.waitlistEntryId));
    }

    // Refresh summary counts after the outcome settles.
    setTimeout(() => requestWaitlistState(), 500);
  }, [clearBackfillPending, requestWaitlistState]);

  const onScheduleUpdatedReceived = useCallback(() => {
    requestWaitlistState();
  }, [requestWaitlistState]);

  const dismissNotification = useCallback(() => {
    setNotification(null);
  }, []);

  // Cleanup all pending timeouts on unmount.
  useEffect(() => {
    return () => {
      backfillTimeouts.current.forEach((t) => clearTimeout(t));
      backfillTimeouts.current.clear();
    };
  }, []);

  return {
    enabled,
    machineState,
    entries,
    pendingBackfills,
    summary,
    backfillPendingMap,
    backfillOutcomeMap,
    notification,
    highlightedEntryId,
    dismissNotification,
    setHighlightedEntryId,
    handleSlotOpened,
    onWaitlistStateReceived,
    onBackfillMatchReceived,
    onBackfillNoMatchReceived,
    onBackfillOutcomeReceived,
    onScheduleUpdatedReceived,
    requestWaitlistState,
  };
}
